import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../../components/AppHeader';
import { useAuth } from '../../contexts/MemberAuthContext';
import { tripsApi, vehiclesApi, eventsApi } from '../../api';
import { TRIP_STATUS, TRIP_STATUS_BADGE, HANDOVER_MODES } from '../../utils/trips';

const emptyForm = {
  vehicle_id: '', trailer_id: '', event_id: '', driver_id: '', origin: '', destination: '',
  fuel_start: '', handover_mode: 'manual', note: '',
};

export default function TripList() {
  const navigate = useNavigate();
  const { member } = useAuth();
  const isOps = ['superadmin', 'ops'].includes(member.role);

  const [trips, setTrips] = useState([]);
  const [overview, setOverview] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [events, setEvents] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  const loadTrips = () => tripsApi.list(statusFilter ? { status: statusFilter } : {})
    .then((r) => setTrips(r.data)).catch(() => setTrips([]));

  useEffect(() => { loadTrips(); }, [statusFilter]);

  useEffect(() => {
    vehiclesApi.list().then((r) => setVehicles(r.data)).catch(() => {});
    eventsApi.list().then((r) => setEvents(r.data)).catch(() => {});
    if (isOps) tripsApi.overview().then((r) => setOverview(r.data)).catch(() => {});
  }, []);

  // Načte řidiče + vleky pro daný spolek (pro výběr v jednotce konvoje).
  const loadUnitOptions = (chapterId) => {
    tripsApi.drivers(isOps ? chapterId : undefined).then((r) => setDrivers(r.data)).catch(() => setDrivers([]));
    vehiclesApi.list(isOps ? chapterId : undefined)
      .then((r) => setTrailers(r.data.filter((v) => v.type === 'trailer')))
      .catch(() => setTrailers([]));
  };

  // chapter_rep zná svůj spolek hned – načti řidiče/vleky při otevření formuláře.
  const openForm = () => {
    setShowForm(true); setForm(emptyForm); setError('');
    setDrivers([]); setTrailers([]);
    if (!isOps && member.chapter_id) loadUnitOptions(member.chapter_id);
  };

  // Po výběru vozidla (u OpS) dourči spolek a načti řidiče/vleky; vlek nesmí = tažné vozidlo.
  const onVehicleChange = (vehicleId) => {
    setForm((f) => ({ ...f, vehicle_id: vehicleId, driver_id: '', trailer_id: '' }));
    const v = vehicles.find((x) => x.id === vehicleId);
    loadUnitOptions(v?.chapter_id);
  };

  const create = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.vehicle_id) { setError('Vyberte vozidlo.'); return; }
    try {
      const payload = {
        vehicle_id: form.vehicle_id,
        trailer_id: form.trailer_id || null,
        event_id: form.event_id || null,
        driver_id: form.driver_id || null,
        origin: form.origin || null,
        destination: form.destination || null,
        fuel_start: form.fuel_start === '' ? null : Number(form.fuel_start),
        handover_mode: form.handover_mode,
        note: form.note || null,
      };
      const res = await tripsApi.create(payload);
      navigate(`/cesty/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Vytvoření selhalo.');
    }
  };

  const fmt = (d) => d ? new Date(d).toLocaleString('cs-CZ', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div>
      <AppHeader subtitle="Přehled cest" />
      <div className="container-wide" style={{ paddingTop: 24 }}>

        {isOps && overview && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
            {Object.entries(TRIP_STATUS).map(([k, label]) => (
              <div key={k} className="card text-center" style={{ cursor: 'pointer', padding: 12 }}
                onClick={() => setStatusFilter(statusFilter === k ? '' : k)}>
                <div style={{ fontSize: 28, fontWeight: 700 }}>{overview[k] ?? 0}</div>
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>{label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div className="flex gap-8 items-center">
            <h2>Cesty</h2>
            <select className="form-input" style={{ width: 'auto', padding: '4px 8px' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">Všechny stavy</option>
              {Object.entries(TRIP_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="flex gap-8">
            <a className="btn btn-outline btn-sm" href={tripsApi.csvUrl(statusFilter ? { status: statusFilter } : {})} download>⬇ CSV</a>
            {!showForm && <button className="btn btn-primary btn-sm" onClick={openForm}>+ Nová cesta</button>}
          </div>
        </div>

        {showForm && (
          <div className="card mb-16">
            <h3 className="mb-16">Nová cesta</h3>
            <form onSubmit={create}>
              <p className="text-muted" style={{ fontSize: '0.82rem', marginTop: -4 }}>
                Toto je <strong>vedoucí vozidlo</strong> konvoje. Další vozidla a vleky přidáš v detailu cesty.
              </p>
              <div className="flex gap-8">
                <div className="form-group flex-1">
                  <label>Vozidlo *</label>
                  <select className="form-input" value={form.vehicle_id} onChange={(e) => onVehicleChange(e.target.value)} required>
                    <option value="">— vyberte —</option>
                    {vehicles.filter((v) => v.type !== 'trailer').map((v) => (
                      <option key={v.id} value={v.id}>{v.name}{v.plate ? ` (${v.plate})` : ''}{v.chapter_name ? ` – ${v.chapter_name}` : ''}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group flex-1">
                  <label>Řidič</label>
                  <select className="form-input" value={form.driver_id} onChange={(e) => setForm({ ...form, driver_id: e.target.value })} disabled={drivers.length === 0}>
                    <option value="">— nezadáno —</option>
                    {drivers.map((d) => <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>)}
                  </select>
                  {drivers.length === 0 && <p className="text-muted" style={{ fontSize: '0.78rem' }}>Nejdřív vyberte vozidlo.</p>}
                </div>
              </div>
              <div className="form-group">
                <label>Vlek <span className="text-muted">(volitelné)</span></label>
                <select className="form-input" value={form.trailer_id} onChange={(e) => setForm({ ...form, trailer_id: e.target.value })} disabled={!form.vehicle_id}>
                  <option value="">— bez vleku —</option>
                  {trailers.map((t) => <option key={t.id} value={t.id}>{t.name}{t.plate ? ` (${t.plate})` : ''}</option>)}
                </select>
              </div>
              <div className="flex gap-8">
                <div className="form-group flex-1">
                  <label>Odkud</label>
                  <input className="form-input" value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} />
                </div>
                <div className="form-group flex-1">
                  <label>Kam</label>
                  <input className="form-input" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} />
                </div>
              </div>
              <div className="flex gap-8">
                <div className="form-group flex-1">
                  <label>Cílová MU <span className="text-muted">(volitelné)</span></label>
                  <select className="form-input" value={form.event_id} onChange={(e) => setForm({ ...form, event_id: e.target.value })}>
                    <option value="">— žádná —</option>
                    {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ width: 140 }}>
                  <label>Stav nádrže (%)</label>
                  <input type="number" min="0" max="100" className="form-input" value={form.fuel_start} onChange={(e) => setForm({ ...form, fuel_start: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label>Režim předání nákladu</label>
                <select className="form-input" value={form.handover_mode} onChange={(e) => setForm({ ...form, handover_mode: e.target.value })}>
                  {Object.entries(HANDOVER_MODES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Poznámka</label>
                <input className="form-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
              </div>
              {error && <div className="alert alert-warning mb-16">{error}</div>}
              <div className="flex gap-8">
                <button type="submit" className="btn btn-primary flex-1">Vytvořit</button>
                <button type="button" className="btn btn-outline" onClick={() => { setShowForm(false); setError(''); }}>Zrušit</button>
              </div>
            </form>
          </div>
        )}

        <div className="table-wrapper">
          <table>
            <thead>
              <tr><th>Vozidlo</th><th>Trasa</th><th>Cílová MU</th><th>Náklad</th><th>Stav</th><th>Výjezd</th></tr>
            </thead>
            <tbody>
              {trips.map((t) => (
                <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/cesty/${t.id}`)}>
                  <td>{t.vehicle_name}{t.vehicle_plate ? <span className="text-muted"> ({t.vehicle_plate})</span> : ''}{t.unit_count > 1 ? <span className="badge badge-in" style={{ marginLeft: 6 }}>konvoj {t.unit_count}×</span> : ''}</td>
                  <td>{[t.origin, t.destination].filter(Boolean).join(' → ') || <span className="text-muted">—</span>}</td>
                  <td>{t.event_name || ''}</td>
                  <td>{t.cargo_count} pol.</td>
                  <td><span className={`badge ${TRIP_STATUS_BADGE[t.status]}`}>{TRIP_STATUS[t.status]}</span></td>
                  <td className="text-muted" style={{ fontSize: '0.85rem' }}>{fmt(t.departed_at)}</td>
                </tr>
              ))}
              {trips.length === 0 && <tr><td colSpan={6} className="text-muted text-center">Žádné cesty.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
